import React, { useEffect } from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import tw from 'tailwind.macro';
import { fadeIn } from '../../../animations/keyframes';
import { slideX, slideY } from '../../../animations/modal';
import {
  disableBodyScroll,
  enableBodyScroll,
} from '../../../helpers/windowScroll';
import { useWindowInnerWidth } from '../../../hooks';
import { screens, screensInt } from '../../../styles/constants';

const slideYFrom = 10;
const slideYTo = 0;

const slideXFrom = -10;
const slideXTo = 0;

const ANIMATION_DURATION = '350ms ease-in-out';

const SecondaryContainerStyle = css`
  ${tw`p-medium2 flex items-center justify-center`}
`;

const SecondaryWrapperStyle = css`
  ${tw`rounded-30 p-medium2`}
  height:auto;
  max-height: 100%;
  overflow-y: scroll;
`;

type ModalVariant = 'SECONDARY';

type ComponentProps = {
  customStyle?: FlattenSimpleInterpolation;
  withoutDesktopModal?: boolean;
  withoutMobileModal?: boolean;
  isMobileFullscreen?: boolean;
  variant?: ModalVariant;
};

const Container = styled.div<ComponentProps>`
  ${tw`bg-overlay z-5 fixed w-full flex pt-big
    md:bg-overlay md:z-5 md:fixed md:pt-0
  `}

  inset: 0;

  ${({ withoutMobileModal }) =>
    withoutMobileModal && tw`static bg-transparent z-0 pt-0`}
  ${({ withoutDesktopModal }) =>
    withoutDesktopModal && tw`md:static md:bg-transparent md:z-0`}
    ${({ isMobileFullscreen }) => isMobileFullscreen && tw`pt-0`}

    ${({ variant }) => {
    switch (variant) {
      case 'SECONDARY':
        return SecondaryContainerStyle;

      default:
        return;
    }
  }}

  ${({ customStyle }) => customStyle}
`;

const Wrapper = styled.div<ComponentProps>`
  ${tw`relative w-full h-full shadow-medium  bg-white rounded-tl-30 rounded-tr-30
   md:rounded-30 md:shadow-light`}

  animation: ${slideY(slideYFrom, slideYTo)} ${ANIMATION_DURATION},
    ${fadeIn} ${ANIMATION_DURATION};

  @media (min-width: ${screens.md}) {
    animation: ${slideX(slideXFrom, slideXTo)} ${ANIMATION_DURATION},
      ${fadeIn} ${ANIMATION_DURATION};

    width: 70vw;
    max-width: 513px;
    max-height: 80vh;
    margin: auto;
  }

  ${({ withoutDesktopModal }) =>
    withoutDesktopModal &&
    css`
      ${tw`md:shadow-none md:m-0`}
    `}

  ${({ withoutMobileModal }) =>
    withoutMobileModal &&
    css`
      @media (max-width: ${screens.md}) {
        animation: none;
        ${tw`shadow-none m-0`}
      }
    `}

    ${({ variant }) => {
    switch (variant) {
      case 'SECONDARY':
        return SecondaryWrapperStyle;

      default:
        return;
    }
  }}

    ${({ customStyle }) => customStyle}
`;

type FunelModalProps = {
  children?: React.ReactNode;
  containerStyle?: FlattenSimpleInterpolation;
  wrapperStyle?: FlattenSimpleInterpolation;
  withoutMobileModal?: boolean;
  withoutDesktopModal?: boolean;
  isMobileFullscreen?: boolean;
  onBackgroundClick?: () => void;
  variant?: ModalVariant;
  testid?: string;
};

const FunelModal = ({
  children,
  containerStyle,
  isMobileFullscreen,
  onBackgroundClick,
  testid,
  variant,
  withoutDesktopModal,
  withoutMobileModal,
  wrapperStyle,
}: FunelModalProps) => {
  const innerWidth = useWindowInnerWidth();

  useEffect(() => {
    if (
      (innerWidth < screensInt.md && !withoutMobileModal) ||
      (innerWidth >= screensInt.md && !withoutDesktopModal)
    ) {
      disableBodyScroll();
    } else {
      enableBodyScroll();
    }

    return () => {
      enableBodyScroll();
    };
  }, [innerWidth, withoutDesktopModal, withoutMobileModal]);

  return (
    <Container
      customStyle={containerStyle}
      data-testid={testid}
      isMobileFullscreen={isMobileFullscreen}
      onClick={() => onBackgroundClick?.()}
      variant={variant}
      withoutDesktopModal={withoutDesktopModal}
      withoutMobileModal={withoutMobileModal}
    >
      <Wrapper
        customStyle={wrapperStyle}
        onClick={(event) => event.stopPropagation()}
        variant={variant}
        withoutDesktopModal={withoutDesktopModal}
        withoutMobileModal={withoutMobileModal}
      >
        {children}
      </Wrapper>
    </Container>
  );
};

export default FunelModal;
